import React, { useCallback, useEffect, useRef, useState } from 'react'
import Cropper from 'react-easy-crop'
import type { Area } from 'react-easy-crop'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import RemoveIcon from '@material-ui/icons/Remove'
import AddIcon from '@material-ui/icons/Add'
import classnames from 'classnames'
import Slider from '../Slider'
import CircularProgress from '../CircularProgress'
import { imageCrossOrigin } from '../../util/corsImage'

const useStyles = makeStyles<Theme, { height: number }>(
  (theme) => ({
    root: {
      width: '100%'
    },
    cropContainer: {
      position: 'relative',
      width: '100%',
      height: ({ height }) => height,
      backgroundColor: theme.palette.grey[100],
      [theme.breakpoints.down('xs')]: {
        height: ({ height }) => Math.round(height * 0.8)
      }
    },
    sliderContainer: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(2, 3),
      gap: theme.spacing(1.5)
    },
    sliderIcon: {
      color: theme.palette.text.secondary,
      fontSize: 20
    },
    slider: {
      flex: 1
    },
    loadingOverlay: {
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.palette.grey[100]
    }
  }),
  { name: 'PhotoCropper' }
)

export interface PhotoCropResult {
  /** The crop as percentages of the photo: `x`/`width` of its width, `y`/`height` of its height */
  area: Area
  /** The crop in the photo's own pixels */
  areaPixels: Area
  /** photo width / height */
  imageAspect: number
  /** False for the crop the cropper reports on load, before anyone dragged or zoomed */
  userChanged: boolean
}

export interface PhotoCropperProps {
  /** URL or data URL of the photo */
  image: string
  /**
   * Where the crop starts, as percentages of the photo; centred and unzoomed when not set. A
   * function gets the photo's aspect (width / height) once it has loaded.
   */
  initialArea?: Area | null | ((imageAspect: number) => Area | null)
  /**
   * Let the photo zoom out until all of it fits inside the circle, leaving blank edges. Off keeps
   * the circle fully on the photo.
   */
  allowZoomOut?: boolean
  maxZoom?: number
  /** Height of the crop area in px */
  height?: number
  className?: string
  onCropComplete: (result: PhotoCropResult) => void
  onError?: () => void
}

/**
 * A round crop over a photo: drag the photo to reposition it, and zoom with the slider, the
 * mouse wheel or a pinch. Used for the profile avatar and the About Your Estimator photo.
 */
function PhotoCropper({
  image,
  initialArea,
  allowZoomOut = false,
  maxZoom = 3,
  height = 350,
  className,
  onCropComplete,
  onError
}: PhotoCropperProps) {
  const classes = useStyles({ height })
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [imageAspect, setImageAspect] = useState<number | null>(null)
  // the URL that loaded: the photo's own, or a cache-busted retry of it
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null)
  // explicit size for a photo smaller than the crop box, which the cropper would show unscaled
  const [mediaSize, setMediaSize] = useState<{ width: number; height: number } | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const userChanged = useRef(false)
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  // Measure the photo before showing the cropper, so the zoom-out limit and the starting crop
  // are right on the first render
  useEffect(() => {
    let cancelled = false
    setImageAspect(null)
    setLoadedSrc(null)
    setMediaSize(null)
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    userChanged.current = false

    // Load with CORS like every other render of the photo, so Safari's shared cache entry stays
    // CORS-clean (docs/pitfalls: media pipeline). Retry once past a poisoned entry.
    const load = (src: string, retried: boolean) => {
      const probe = new Image()
      probe.crossOrigin = imageCrossOrigin()
      probe.addEventListener('load', () => {
        if (cancelled) return
        const aspect = probe.naturalWidth / probe.naturalHeight || 1
        const box = containerRef.current?.getBoundingClientRect()
        if (box?.width && box.height) {
          const width = Math.min(box.width, box.height * aspect)
          if (probe.naturalWidth < width) setMediaSize({ width, height: width / aspect })
        }
        setLoadedSrc(src)
        setImageAspect(aspect)
      })
      probe.addEventListener('error', () => {
        if (cancelled) return
        if (!retried && /^https?:/.test(src)) {
          load(src + (src.includes('?') ? '&' : '?') + '__ps_cors=1', true)
          return
        }
        onErrorRef.current?.()
      })
      probe.src = src
    }
    load(image, false)

    return () => {
      cancelled = true
    }
  }, [image])

  const minZoom = allowZoomOut && imageAspect ? Math.min(imageAspect, 1 / imageAspect) : 1

  const handleCropComplete = useCallback(
    (area: Area, areaPixels: Area) => {
      onCropComplete({ area, areaPixels, imageAspect: imageAspect ?? 1, userChanged: userChanged.current })
    },
    [onCropComplete, imageAspect]
  )

  const markChanged = () => {
    userChanged.current = true
  }

  return (
    // Stop clicks and keys here so a surrounding file dropzone doesn't open its file picker
    <div
      className={classnames(classes.root, className)}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div ref={containerRef} className={classes.cropContainer}>
        {imageAspect && loadedSrc ? (
          <Cropper
            image={loadedSrc}
            mediaProps={{ crossOrigin: imageCrossOrigin() }}
            crop={crop}
            zoom={zoom}
            minZoom={minZoom}
            maxZoom={maxZoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            restrictPosition={!allowZoomOut}
            initialCroppedAreaPercentages={
              (typeof initialArea === 'function' ? initialArea(imageAspect) : initialArea) ?? undefined
            }
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onInteractionStart={markChanged}
            // onCropAreaChange, not onCropComplete: a drag reports completion before its last frame
            // is applied, so a quick drag-and-release would report the crop from one frame early
            onCropAreaChange={handleCropComplete}
            style={mediaSize ? { mediaStyle: mediaSize } : undefined}
          />
        ) : (
          <div className={classes.loadingOverlay}>
            <CircularProgress />
          </div>
        )}
      </div>
      <div className={classes.sliderContainer}>
        <RemoveIcon className={classes.sliderIcon} />
        <div className={classes.slider}>
          <Slider
            value={zoom}
            min={minZoom}
            max={maxZoom}
            step={0.05}
            aria-label="Zoom"
            onChange={(_e, value) => {
              markChanged()
              setZoom(value as number)
            }}
          />
        </div>
        <AddIcon className={classes.sliderIcon} />
      </div>
    </div>
  )
}

export default PhotoCropper
